/**
 * @fileoverview AppPortalDialog — Foundry's apps launcher modal. A Blueprint
 * `Dialog` whose top bar IS the search field (full-width, close button on the
 * right — no separate title), over the Foundry-faithful body:
 *
 *   ┌ [🔍 search ........................] [Filters] [✕] ┐  ← top bar = search
 *   │ left-side (250px)  │ right-side                     │
 *   │   navigation       │   results (grouped) │ details  │
 *   └────────────────────┴────────────────────────────────┘
 *
 * Foundry mapping: `application-portal-dialog__modal-apps` (shell),
 * `__dialog-header` (the search top bar), `__apps-content`, `__left-side`,
 * `__right-side`, `__result-section-container`, `__result-section`,
 * `__details-panel`.
 */

import { applicationPortalDialog as fdy } from "./foundry-parts.generated";
import { Dialog } from "@blueprintjs/core";

import type { ReactNode } from "react";

interface AppPortalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Search bar that forms the dialog's full-width top header. */
  search: ReactNode;
  /** Left rail of categories. */
  navigation: ReactNode;
  /** Middle column of grouped result lists. */
  results: ReactNode;
  /** Right details panel for the currently-selected app. */
  detailsPanel?: ReactNode;
  className?: string;
}

export function AppPortalDialog({
  isOpen,
  onClose,
  search,
  navigation,
  results,
  detailsPanel,
  className,
}: AppPortalDialogProps) {
  return (
    <Dialog
      data-testid="app-portal-dialog"
      isOpen={isOpen}
      onClose={onClose}
      /*
        FOUNDRY'S OWN CLASS ALONGSIDE OURS. `fdy["modal-apps"]` is the exact
        `application-portal-dialog__modal-apps__1d3zmg1` the product ships, and
        the vendored stylesheet keys on it — so the dialog's box, surface, type
        and radius come from Foundry's CSS rather than values retyped here.
        `bp-app-portal-dialog` stays because our own selectors and tests use it.
      */
      className={[fdy["modal-apps"], "bp-app-portal-dialog", "bp6-dark", className]
        .filter(Boolean)
        .join(" ")}
      // Size via the Dialog `style` prop (inline) rather than CSS: Blueprint's
      // own `.bp6-dialog { width: 500px }` ships unlayered and would beat our
      // @layer ironcase rules, collapsing the modal to its min-width.
      // Foundry .ic-app-portal-dialog: 92vw/max 1200 wide, 80vh/max 760 tall.
      /*
        THE DECLARED RULE, read out of the captured CSS rather than inferred:

          width: 90vw; min-width: 600px; max-width: 1300px;
          height: 90vh; max-height: 750px;

        There are no @media blocks for it — that is the whole responsive story.

        Three wrong answers preceded this one, each from a weaker source: 1296
        from the reference PNG (framed by whoever captured it), 1260 from the
        CSSOM at a single viewport (a sample of a function), and "height fixed at
        750" from sampling three viewports that were all 900px tall — so 90vh
        clamped to 750 every time and the samples agreed for the wrong reason.
        A declared rule states the function; a measurement only ever shows one
        of its values.
      */
      style={{
        width: "90vw",
        minWidth: 600,
        maxWidth: 1300,
        height: "90vh",
        maxHeight: 750,
      }}
    >
      <div className={`${fdy["dialog-header"]} bp-app-portal-dialog-topbar`}>{search}</div>
      <div className={`${fdy["apps-content"]} bp-app-portal-dialog-content`}>
        <div className={`${fdy["left-side"]} bp-app-portal-dialog-left`}>{navigation}</div>
        <div className={`${fdy["right-side"]} bp-app-portal-dialog-right`}>
          <div className={`${fdy["result-section-container"]} bp-app-portal-dialog-results-container`}>
            <div className={`${fdy["result-section"]} bp-app-portal-dialog-results`}>{results}</div>
            {detailsPanel && (
              /*
                THE PANEL HAS NO PADDING AND NO SCROLLER. Foundry's
                `__details-panel__` computes `padding: 0` and `overflow: visible`
                — it is a 300px column and nothing else; the spacing and any
                scrolling belong to whatever is rendered inside it. Ours put
                24px/20px and `overflow-y: auto` on the column itself, which
                inset the pane's own border and gave it a second scrollbar
                alongside the results list.

                Handled by a `> *` rule rather than a wrapper element: the two
                things that go in here are the detail view and an empty state,
                neither of which shares a class, and adding a div to hold the
                padding would put a node in the tree that Foundry does not have.
              */
              <div className={`${fdy["details-panel"]} bp-app-portal-dialog-details`}>
                {detailsPanel}
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
